import { useState } from 'react';
import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { TokenPagedQueryHook } from './token-paged-query';

export type UseTokenPaginationReturn = {
  data: GetPatientsBalanceResponse_PatientBalance[];
  isLoading: boolean; // True whenever data is being fetched (including pagination/filtering)
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentPage: number; // 1-indexed, for display
  pageSize: number;
  orderBy?: string; // Current sort order (e.g., "balance desc, first_name asc")
  goNext: () => void;
  goPrev: () => void;
  setPageSize: (size: number) => void;
  setOrderBy: (orderBy?: string) => void;
};

type UseTokenPaginationParams = {
  enabled: boolean;
  /** Serialized filter state. Any change resets to page 1 before the next request goes out. */
  filterKey: string;
  defaultPageSize: number;
  useQuery: TokenPagedQueryHook;
};

export const useTokenPagination = ({
  enabled,
  filterKey,
  defaultPageSize,
  useQuery,
}: UseTokenPaginationParams): UseTokenPaginationReturn => {
  // Stack: index 0 = first page (no token). Each entry is the pageToken for that page.
  // pageTokenStack[0] is always undefined (first page has no token).
  // pageTokenStack[n] is the nextPageToken returned from page n-1.
  const [pageTokenStack, setPageTokenStack] = useState<(string | undefined)[]>([undefined]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);
  const [orderBy, setOrderByState] = useState<string | undefined>();
  const [tokenFilterKey, setTokenFilterKey] = useState(filterKey);

  // Reset to page 1 when filters change. Done during render (not in an effect) so the query
  // never fires with a page token that belongs to the previous filters.
  const isPageTokenStale = tokenFilterKey !== filterKey;
  if (isPageTokenStale) {
    setTokenFilterKey(filterKey);
    setPageTokenStack([undefined]);
    setCurrentPageIndex(0);
  }

  const pageIndex = isPageTokenStale ? 0 : currentPageIndex;

  const {
    data,
    isFetching,
    nextPageToken: rawNextPageToken,
  } = useQuery({
    enabled,
    pageToken: isPageTokenStale ? undefined : pageTokenStack[currentPageIndex],
    pageSize,
    orderBy,
    keepPreviousData: true,
  });

  const nextPageToken = isFetching ? undefined : rawNextPageToken;

  const goNext = () => {
    if (isFetching || !nextPageToken) return;
    setPageTokenStack((prev) => {
      const next = [...prev];
      // Only push if we haven't already stored this token
      if (next.length <= currentPageIndex + 1) {
        next.push(nextPageToken);
      }
      return next;
    });
    setCurrentPageIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (currentPageIndex === 0) return;
    setCurrentPageIndex((i) => i - 1);
  };

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPageTokenStack([undefined]);
    setCurrentPageIndex(0);
  };

  const setOrderBy = (newOrderBy?: string) => {
    setOrderByState(newOrderBy);
    setPageTokenStack([undefined]);
    setCurrentPageIndex(0);
  };

  return {
    data,
    isLoading: isFetching,
    hasNextPage: !!nextPageToken,
    hasPreviousPage: pageIndex > 0,
    currentPage: pageIndex + 1,
    pageSize,
    orderBy,
    goNext,
    goPrev,
    setPageSize,
    setOrderBy,
  };
};
